import React, { Fragment, useState, FC, useEffect, useMemo, useCallback } from "react";
import { Popover, Transition } from "@headlessui/react";
import { CalendarIcon } from "@heroicons/react/24/outline";
import DatePicker from "react-datepicker";
import ClearDataButton from "components/HeroSearchForm/ClearDataButton";
import DatePickerCustomHeaderTwoMonth from "components/DatePickerCustomHeaderTwoMonth";
import DatePickerCustomDay from "components/DatePickerCustomDay";
import { useBooking } from "context/BookingContext";
import { addDays, parseISO, isSameDay, format as formatDate } from "date-fns";
import { useListingBooking } from "context/availabilitiesContext";
import { useClient } from "ClientContext";

import { useTranslation } from "react-i18next";

export interface StayDatesRangeInputProps {
  className?: string;
  /**
   * Add this callback to notify parent when dates change
   */
  onDatesChange?: () => void; // <-- ADDED
  dates: string[];
}

const TODAY = new Date();
const BookingQuoteCalendar: FC<StayDatesRangeInputProps> = ({
  className = "flex-1",
  onDatesChange, // <-- ADDED
  dates
}) => {
  const { t } = useTranslation();
  const { integration_TYPE } = useClient();
  const { availabilitiesState } = useListingBooking();
  const { bookingState, setBookingState } = useBooking();
  const { searchParams } = bookingState;

  // Initialize state with default values or from searchParams
  const [startDate, setStartDate] = useState<Date | null>(
    searchParams?.checkIn || new Date()
  );
  const [endDate, setEndDate] = useState<Date | null>(
    searchParams?.checkOut || addDays(new Date(), 5)
  );

  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  // Normalize all incoming date strings to local-midnight Date objects
  const allLocalMidnights: Date[] = useMemo(() => {
    return dates.map(dateStr => {
      const utcDate = parseISO(dateStr);
      return new Date(
        utcDate.getUTCFullYear(),
        utcDate.getUTCMonth(),
        utcDate.getUTCDate()
      );
    });
  }, [dates]);

  // Optimize disabled dates computation
  const disabledDates: Date[] = useMemo(() => {
    if (!startDate) {
      return allLocalMidnights;
    }

    // Find the next disabled date after startDate more efficiently
    const sortedDates = allLocalMidnights
      .filter(date => date > startDate)
      .sort((a, b) => a.getTime() - b.getTime());
    
    const nextDisabledDate = sortedDates[0];

    return allLocalMidnights.filter(date => {
      // Enable check-in date
      if (isSameDay(date, startDate)) {
        return false;
      }
      
      // Enable the next disabled date after check-in
      if (nextDisabledDate && isSameDay(date, nextDisabledDate)) {
        return false;
      }

      return true;
    });
  }, [allLocalMidnights, startDate]);

  // Update local state when booking state changes
  useEffect(() => {
    const { checkIn, checkOut } = searchParams || {};
    setStartDate(checkIn || null);
    setEndDate(checkOut || null);
  }, [searchParams]);

  // Wait until availabilities data is available
  useEffect(() => {
    const fetchDisabledDates = async () => {
      setLoading(true);
      try {
        //setDisabledDates(dates);
      } catch (err) {
        setError(`Error fetching dates: ${(err as Error).message}`);
      } finally {
        setLoading(false);
      }
    };

    fetchDisabledDates();
  }, [availabilitiesState, integration_TYPE]);

  // Memoize the date change handler
  const onChangeDate = useCallback((dates: [Date | null, Date | null]) => {
    const [start, end] = dates;
    setStartDate(start);
    setEndDate(end);

    setBookingState((prevState) => ({
      ...prevState,
      searchParams: {
        ...prevState.searchParams,
        checkIn: start,
        checkOut: end,
      },
    }));

    // Trigger the parent callback to notify date change
    onDatesChange?.(); // <-- ADDED
  }, [setBookingState, onDatesChange]);

  // Memoize the input renderer
  const renderInput = useCallback(() => (
    <div className="flex-grow text-left">
      <span className="block xl:text-lg font-semibold">
        {startDate?.toLocaleDateString("en-US", {
          month: "short",
          day: "2-digit",
        }) || "Add dates"}
        {endDate
          ? ` - ${endDate.toLocaleDateString("en-US", {
              month: "short",
              day: "2-digit",
            })}`
          : ""}
      </span>
      <span className="block mt-1 text-sm text-neutral-400 leading-none font-light">
        {`${t("Check in")} - ${t("Check out")}`}
      </span>
    </div>
  ), [startDate, endDate, t]);

  // Memoize custom header to prevent unnecessary re-renders
  const renderCustomHeader = useCallback(
    (props: any) => <DatePickerCustomHeaderTwoMonth {...props} />,
    []
  );

  // Memoize day contents renderer
  const renderDayContents = useCallback(
    (day: number, date?: Date) => <DatePickerCustomDay dayOfMonth={day} date={date} />,
    []
  );

  const renderLoading = () => <div>Loading...</div>;
  const renderError = () => <div>Error: {error}</div>;

  // Check if critical data is undefined, display loading or error if necessary
  if (loading || !availabilitiesState || !integration_TYPE || error) {
    return loading ? renderLoading() : renderError();
  }

  return (
    <Popover className={`StayDatesRangeInput z-10 relative flex ${className}`}>
      {({ open, close }) => (
        <>
          <Popover.Button
            className={`flex-1 flex relative p-3 items-center space-x-3 focus:outline-none ${
              open ? "shadow-lg" : ""
            }`}
          >
            <div className="text-neutral-300 dark:text-neutral-400">
              <CalendarIcon className="w-5 h-5 lg:w-7 lg:h-7" />
            </div>
            {renderInput()}
            {startDate && open && (
              <ClearDataButton onClick={() => onChangeDate([null, null])} />
            )}
          </Popover.Button>

          <Transition
            as={Fragment}
            enter="transition ease-out duration-200"
            enterFrom="opacity-0 translate-y-1"
            enterTo="opacity-100 translate-y-0"
            leave="transition ease-in duration-150"
            leaveFrom="opacity-100 translate-y-0"
            leaveTo="opacity-0 translate-y-1"
          >
            <Popover.Panel className="absolute right-0 xl:-right-10 z-50 top-full mt-3 w-[95vw] lg:w-screen max-w-md lg:max-w-3xl px-2 lg:px-0">
              <div className="overflow-hidden rounded-3xl shadow-lg ring-1 ring-black ring-opacity-5 bg-white dark:bg-neutral-800 p-8">
                <DatePicker
                  minDate={TODAY}
                  selected={startDate}
                  onChange={(dates) => {
                    onChangeDate(dates);
                    // Close the popover when both dates are selected
                    const [start, end] = dates;
                    if (start && end) {
                      close();
                    }
                  }}
                  startDate={startDate}
                  endDate={endDate}
                  selectsRange
                  monthsShown={2}
                  showPopperArrow={false}
                  inline
                  excludeDates={disabledDates}
                  renderCustomHeader={renderCustomHeader}
                  renderDayContents={renderDayContents}
                />
              </div>
            </Popover.Panel>
          </Transition>
        </>
      )}
    </Popover>
  );
};

export default BookingQuoteCalendar;